import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiShare2, FiList, FiXCircle, FiActivity } from 'react-icons/fi';
import DataTable from '../../../components/DataTable';
import { useAdminAssessment, steps } from '../../../context/AdminAssessmentContext';
import { useAuth } from '../../../context/AuthContext';
import { HierarchyProvider, useHierarchy } from '../../../context/HierarchyContext';
import HierarchySelector from '../../../components/HierarchySelector';
import HierarchyBadge from '../../../components/HierarchyBadge';
import AssessmentFormModal from './AssessmentFormModal';
import DeleteAssessmentModal from './DeleteAssessmentModal';
import RevokeAccessModal from '../Assessments/RevokeAccessModal';
import ShareAssessmentModal from './ShareAssessmentModal';
import CrosswalkDetailsModal from './CrosswalkDetailsModal';

const AssessmentsInner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    // State
    assessments,
    loading,
    error,
    formData,
    isEditing,
    currentStep,
    validationErrors,
    selectedAssessment,
    selectedIds,
    isModalOpen,
    isDeleteModalOpen,
    isRevokeModalOpen,
    isShareModalOpen,
    revokeShareDetails,
    revokeLoading,
    revokeSelectedOrgIds,
    revokeSelectedUserIds,
    shareOrganizations,
    shareUsers,
    shareFilteredUsers,
    shareSelectedOrgs,
    shareSelectedUsers,
    shareAssessmentIds,
    shareLoading,
    shareDataLoading,
    shareUserSearch,
    organizations,
    vendors,
    projects,
    categories,
    domains,
    departments,
    disciplines,
    focusAreas,
    industries,
    toast,

    // Actions
    fetchAssessments,
    toggleSelect,
    toggleSelectAll,
    openCreateModal,
    openEditModal,
    openDeleteModal,
    openRevokeModal,
    openShareModal,
    handleInputChange,
    handlePhoneChange,
    handleNext,
    handlePrevious,
    handleSubmit,
    handleDelete,
    handleRevoke,
    toggleRevokeOrg,
    toggleRevokeUser,
    toggleShareOrg,
    toggleShareUser,
    toggleSelectAllUsers,
    toggleShareAssessment,
    handleShare,
    deleteReferences,
    loadingReferences,
    setIsModalOpen,
    setIsDeleteModalOpen,
    setIsRevokeModalOpen,
    setIsShareModalOpen,
    setCurrentStep,
    setToast,
    setRevokeSelectedOrgIds,
    setRevokeSelectedUserIds,
    setShareAssessmentIds,
    setShareUserSearch,
  } = useAdminAssessment();

  // Hierarchy scope
  const {
    hierarchyLevel,
    filterAssessmentsByScope,
    scopeLabel,
    isEnterpriseAdmin,
    isRestricted,
    userOwnDept,
    userOwnDiscipline,
  } = useHierarchy();

  // A non-admin user with no department configured cannot see anything.
  const isMisconfiguredUser = isRestricted && !userOwnDept;

  const [crosswalkDetailsAssessment, setCrosswalkDetailsAssessment] = useState(null);

  // Helper functions for styling
  const isAllSelected = assessments.length > 0 && selectedIds.length === assessments.length;

  const getRiskCircleColor = (rating) => {
    const colors = {
      1: 'bg-[#53545a] text-white',
      2: 'bg-[#F47F2E] text-white',
      3: 'bg-[#919191] text-white',
      4: 'bg-[#0077b5] text-white'
    };
    return colors[rating] || 'bg-[#919191] text-white';
  };

  const getOwnerInitial = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };
  const columns = useMemo(() => [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={toggleSelectAll}
          className="w-4 h-4 rounded border-[#919191]/50 text-[#0077b5] focus:ring-[#0077b5] cursor-pointer"
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.id)}
          onChange={() => toggleSelect(row.id)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 rounded border-[#919191]/50 text-[#0077b5] focus:ring-[#0077b5] cursor-pointer"
        />
      ),
      className: 'w-10',
    },
    {
      key: 'vendorName',
      header: 'Business Organization',
      sortable: true,
      filterable: true,
      accessor: (row) => row.vendorName || '',
      render: (row) => <span className="text-sm text-[#53545a]">{row.vendorName || '-'}</span>,
    },
    {
      key: 'accountName',
      header: 'Account Name',
      sortable: true,
      filterable: true,
      accessor: (row) => row.accountName || '',
      render: (row) => <span className="text-sm font-medium text-[#53545a]">{row.accountName || '-'}</span>,
    },
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      filterable: true,
      accessor: (row) => row.assessmentDescription || '',
      render: (row) => <span className="text-sm text-[#919191]">{row.assessmentDescription || '-'}</span>,
    },
    {
      key: 'customerOwner',
      header: 'Customer Owner',
      sortable: true,
      filterable: true,
      accessor: (row) => row.customerOwner || '',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#0077b5] text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {getOwnerInitial(row.customerOwner)}
          </div>
          <span className="text-sm text-[#53545a]">{row.customerOwner || '-'}</span>
        </div>
      ),
    },
    {
      key: 'projectName',
      header: 'Assessment Project',
      sortable: true,
      filterable: true,
      accessor: (row) => row.projectName || '',
      render: (row) => <span className="text-sm text-[#919191]">{row.projectName || '-'}</span>,
    },
    {
      key: 'riskRating',
      header: 'Risk Rating',
      sortable: true,
      filterable: true,
      accessor: (row) => String(row.riskRating || 0),
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${getRiskCircleColor(row.riskRating)}`}>
            {row.riskRating || 0}
          </span>
        </div>
      ),
    },
    {
      key: 'questionnaires',
      header: 'Questionnaires',
      headerClassName: 'text-center',
      className: 'text-center',
      render: (row) => (
        <div className="flex justify-center">
          <button
            onClick={() => navigate(`/questionnaires?assessmentId=${row.id}`)}
            className="relative w-10 h-10 rounded-full bg-[#F47F2E]/10 hover:bg-[#F47F2E] text-[#F47F2E] hover:text-white flex items-center justify-center transition-all duration-200"
            title="View questionnaires"
          >
            <FiList className="w-5 h-5" />
            <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] flex items-center justify-center px-1 rounded-full bg-[#F47F2E] text-white text-[10px] font-bold leading-none ring-2 ring-white">
              {row.questionnaireCount || 0}
            </span>
          </button>
        </div>
      ),
    },
  ], [selectedIds, isAllSelected, navigate]);

  // Action menu items per row
  const getActions = (assessment) => {
    const items = [
      { label: 'Share', icon: FiShare2, onClick: () => openShareModal([assessment.id]) },
      { label: 'Edit', icon: FiEdit2, onClick: () => openEditModal(assessment) },
      { label: 'Questionnaires', icon: FiList, onClick: () => navigate(`/questionnaires?assessmentId=${assessment.id}`) },
      { label: 'Crosswalk Details', icon: FiActivity, onClick: () => setCrosswalkDetailsAssessment(assessment) },
      { divider: true },
    ];
    
    const isAdmin = user?.role === 'admin';
    const isOwnedByOrg = !assessment.isShared;
    if (isAdmin && isOwnedByOrg && assessment.hasSharedWithOthers) {
      items.push({ label: 'Revoke Access', icon: FiXCircle, onClick: () => openRevokeModal(assessment), danger: true });
    }
    
    if (!assessment.isShared) {
      items.push({ label: 'Delete', icon: FiTrash2, onClick: () => openDeleteModal(assessment), danger: true });
    }
    
    return items;
  };



  // Filter assessments visible to department_admin to their own department only.
  // Enterprise users (no department) see everything.
  const isDeptAdmin =
    (Array.isArray(user?.roles) && (user.roles.includes('department_admin') || user.roles.includes('departmentadmin'))) ||
    user?.role === 'department_admin' || user?.role === 'departmentadmin';
  const userDept = (user?.department || '').trim().toLowerCase();
  const isEnterprise = !userDept || userDept === 'enterprise';

  // Apply hierarchy scope filter on top of role-based filter
  const scopedAssessments = useMemo(
    () => filterAssessmentsByScope(assessments),
    [assessments, filterAssessmentsByScope]
  );

  const shareableAssessments = useMemo(() => {
    if (!isDeptAdmin || isEnterprise) return scopedAssessments;
    return scopedAssessments.filter(
      a => !a.departmentName || a.departmentName.trim().toLowerCase() === userDept
    );
  }, [scopedAssessments, isDeptAdmin, isEnterprise, userDept]);

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-[#53545a]">Assessments</h1>
          <HierarchyBadge level={hierarchyLevel} />
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#F47F2E] text-white">
            {scopedAssessments.length} of {assessments.length}
          </span>
        </div>
        {scopeLabel && (
          <span className="text-xs text-slate-500">{scopeLabel}</span>
        )}
      </div>

      {/* Role-based subtitle */}
      <p className="text-sm text-[#919191]">
        {isEnterpriseAdmin
          ? 'View all assessments, create new ones, filter by scope, and share them with users.'
          : 'View assessments for your organization, create new ones, filter by scope, and share them with users under your organization.'}
      </p>

      {/* Hierarchy Scope Selector — only enterprise admins can change scope */}
      {isEnterpriseAdmin && <HierarchySelector />}

      {/* Scope notice for restricted users (read-only) */}
      {isRestricted && !isMisconfiguredUser && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Viewing assessments scoped to your profile:
          <span className="ml-1 font-semibold text-slate-700">
            {userOwnDept}
            {userOwnDiscipline ? ` › ${userOwnDiscipline}` : ''}
          </span>
        </div>
      )}

      {/* Misconfigured-user warning (no department) */}
      {isMisconfiguredUser && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-semibold">No assessments available.</span> Your
          user profile is missing a department assignment. Please contact your
          administrator to configure your department (and discipline, if
          applicable) before you can view assessments.
        </div>
      )}

      {/* Page-level skeleton while loading */}
      {loading && (
        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="w-80 h-9 bg-[#e6eef5] rounded animate-pulse mb-2" />
            </div>
            <div className="flex items-center justify-end gap-2">
              <div className="w-24 h-10 bg-[#e6eef5] rounded animate-pulse" />
              <div className="w-64 h-10 bg-[#e6eef5] rounded animate-pulse" />
              <div className="w-40 h-10 bg-[#e6eef5] rounded animate-pulse" />
            </div>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={scopedAssessments}
        rowKey="id"
        loading={loading}
        error={error}
        onRetry={fetchAssessments}
        searchable
        searchPlaceholder="Search assessments..."
        actions={getActions}
        headerActions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => openShareModal(selectedIds)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border transition-colors border-[#F47F2E] text-[#F47F2E] hover:bg-[#F47F2E]/10`}
            >
              <FiShare2 className="text-base" /> Share Selected Assessment {selectedIds.length > 0 && `(${selectedIds.length})`}
            </button>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white text-sm font-medium hover:bg-[#e06a1f] transition-colors shadow-sm"
            >
              <FiPlus className="text-base" /> Add Assessment
            </button>
          </div>
        }
        totalLabel="assessments"
        emptyMessage="No assessments found"
      />

      {/* Form Modal */}
      <AssessmentFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setCurrentStep(0);
        }}
        isEditing={isEditing}
        selectedAssessment={selectedAssessment}
        formData={formData}
        validationErrors={validationErrors}
        currentStep={currentStep}
        organizations={organizations}
        vendors={vendors}
        projects={projects}
        categories={categories}
        domains={domains}
        departments={departments}
        disciplines={disciplines}
        focusAreas={focusAreas}
        industries={industries}
        onInputChange={handleInputChange}
        onPhoneChange={handlePhoneChange}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSubmit={handleSubmit}
        onStepChange={setCurrentStep}
      />

      {/* Delete Modal */}
      <DeleteAssessmentModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDelete}
        references={deleteReferences}
        loadingReferences={loadingReferences}
      />

      {/* Revoke Modal */}
      <RevokeAccessModal
        isOpen={isRevokeModalOpen}
        onClose={() => setIsRevokeModalOpen(false)}
        selectedAssessment={selectedAssessment}
        revokeShareDetails={revokeShareDetails}
        revokeLoading={revokeLoading}
        revokeSelectedOrgIds={revokeSelectedOrgIds}
        revokeSelectedUserIds={revokeSelectedUserIds}
        onRevoke={handleRevoke}
        onToggleOrg={toggleRevokeOrg}
        onToggleUser={toggleRevokeUser}
        onSelectAll={() => {
          const allUserIds = revokeShareDetails.organizations.flatMap(o => o.users.map(u => u.userId));
          const allOrgIds = revokeShareDetails.organizations.map(o => o.organizationId);
          setRevokeSelectedUserIds(allUserIds);
          setRevokeSelectedOrgIds(allOrgIds);
        }}
        onDeselectAll={() => {
          setRevokeSelectedUserIds([]);
          setRevokeSelectedOrgIds([]);
        }}
      />

      {/* Share Modal */}
      <ShareAssessmentModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        assessments={shareableAssessments}
        departmentLabel={isDeptAdmin && !isEnterprise ? user?.department : null}
        shareAssessmentIds={shareAssessmentIds}
        shareOrganizations={shareOrganizations}
        shareSelectedOrgs={shareSelectedOrgs}
        shareSelectedUsers={shareSelectedUsers}
        shareFilteredUsers={shareFilteredUsers}
        shareUserSearch={shareUserSearch}
        shareLoading={shareLoading}
        shareDataLoading={shareDataLoading}
        onShare={handleShare}
        onToggleAssessment={toggleShareAssessment}
        onSelectAllAssessments={() => setShareAssessmentIds(shareableAssessments.map(a => a.id))}
        onDeselectAllAssessments={() => setShareAssessmentIds([])}
        onToggleOrg={toggleShareOrg}
        onToggleUser={toggleShareUser}
        onToggleSelectAllUsers={toggleSelectAllUsers}
        onUserSearchChange={setShareUserSearch}
      />

      {/* Crosswalk Details Modal */}
      <CrosswalkDetailsModal
        isOpen={!!crosswalkDetailsAssessment}
        onClose={() => setCrosswalkDetailsAssessment(null)}
        assessment={crosswalkDetailsAssessment}
      />

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-6 right-6 z-[99999] px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${
          toast.type === 'success' ? 'bg-green-600' : toast.type === 'warning' ? 'bg-amber-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

const Assessments = () => (
  <HierarchyProvider>
    <AssessmentsInner />
  </HierarchyProvider>
);

export default Assessments;
